import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, translations, detectLanguage, setLanguage as saveLanguage } from '@/lib/i18n';
import { supabase } from '@/integrations/supabase/client';

// Extended type that includes dynamically merged properties (app, auth)
type TranslationType = typeof translations.pt & {
  app?: any;
  auth?: any;
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  setLanguageTransient: (lang: Language) => void;
  t: TranslationType;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>('pt');
  const [hasDetected, setHasDetected] = useState(false);

  useEffect(() => {
    const initLanguage = async () => {
      // Check if user has manual preference
      const stored = localStorage.getItem('language') as Language;
      if (stored && ['pt', 'es', 'en'].includes(stored)) {
        setLanguageState(stored);
        setHasDetected(true);
        return;
      }

      // Check if we already auto-detected
      const autoDetected = localStorage.getItem('language_auto') as Language;
      if (autoDetected && ['pt', 'es', 'en'].includes(autoDetected)) {
        setLanguageState(autoDetected);
        setHasDetected(true);
        return;
      }

      // Try to detect from server (geo-based)
      try {
        const { data, error } = await supabase.functions.invoke('detect-locale');
        if (!error && data?.languageSuggested) {
          const suggested = data.languageSuggested as Language;
          if (['pt', 'es', 'en'].includes(suggested)) {
            setLanguageState(suggested);
            localStorage.setItem('language_auto', suggested);
            console.log(`Auto-detected language: ${suggested} from ${data.countryCode || 'headers'}`);
          }
        }
      } catch (e) {
        console.log('Language detection fallback to browser');
      }

      // Fallback to browser detection
      if (!hasDetected) {
        const detected = detectLanguage();
        setLanguageState(detected);
      }
      setHasDetected(true);
    };

    initLanguage();
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    saveLanguage(lang);
    // Clear auto-detection when user manually selects
    localStorage.removeItem('language_auto');
  };

  const setLanguageTransient = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, setLanguageTransient, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Fallback translations for when context is not yet available
const fallbackContext: LanguageContextType = {
  language: 'pt',
  setLanguage: () => {},
  setLanguageTransient: () => {},
  t: translations.pt as TranslationType,
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  
  // Return fallback instead of throwing during initial render or hot reload
  if (!context) {
    console.warn('useLanguage called outside LanguageProvider, using fallback');
    return fallbackContext;
  }
  
  return context;
};
